import axios from "axios";
import { environment } from "../../environments/environment";
import { ListResponse } from "./utils";
import { Course } from "./courses";
import { Expirence } from "./experiences";
import { Candidate } from "./candidates";
import { logger } from "./analytics";
import { isNetworkError, NetworkError } from "../utils/errors";

interface JobApply {
    id: number,
    jobpost_id: number,
    candidate_id: string,
    status: "applied" | "interview" | "rejected" | "not_fit" | "hired" | "fulfilled" | "cancelled",
    reason_id?: number,
    interview_date?: string,
    hired_date?: string,
    courses?: Course[],
    experiences?: Expirence[],
    candidate?: Candidate
}

interface JobApplyStatus {
    status: "applied" | "interview" | "rejected" | "not_fit" | "hired" | "fulfilled" | "cancelled",
    reason_id?: number,
    interview_date?: string,
    hired_date?: string
}

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const retryOnNetworkError = async <T,>(
    fn: () => Promise<T>,
    retries: number,
    context: Record<string, any>
): Promise<T> => {
    try {
        return await fn();
    } catch (error) {
        if (retries > 0 && isNetworkError(error)) {
            logger.warn('Erro de rede ao buscar candidaturas - tentando novamente', {
                ...context,
                remaining_retries: retries,
                category: 'api'
            });
            await delay(800);
            return retryOnNetworkError(fn, retries - 1, context);
        }
        throw error;
    }
};

export const applyToJob = async (jobpost_id: number): Promise<any> => {
    const response = await axios.post<any>(environment.apiUrl+'/api/jobapply/', {jobpost_id}, {
        headers: {
            'Content-Type': 'application/json'
        }
    });

    if(response.status !== 201) {
        throw Error('error');
    };

    return response.data;
};

export const jobApplies = async (params?: any): Promise<ListResponse<any>> => {
    const response = await axios.get<any[]>(environment.apiUrl+'/api/jobapply/', {
        headers: {
            'Content-Type': 'application/json'
        }
    });

    if(response.status !== 200) {
        throw Error('error');
    };

    return { results: response.data, count: response.data.length };
};

export const retriveJobApply = async (id: any): Promise<JobApply> => {
    const response = await axios.get<any>(environment.apiUrl+'/api/jobapply/'+id, {
        headers: {
            'Content-Type': 'application/json'
        }
    });

    if(response.status !== 200) {
        throw Error('error');
    };

    return response.data;
};

export const updateJobApply = async (id: any, body: JobApplyStatus): Promise<any> => {
    const response = await axios.put<any>(environment.apiUrl+'/api/jobapply/'+id, 
        body,
        {
        headers: {
            'Content-Type': 'application/json'
        }
    });

    if(response.status !== 204) {
        throw Error('error');
    };

    return response.data;
};

export const listJobApplies = async (id: any): Promise<any[]> => {
    logger.info('Buscando candidaturas de uma vaga via API', {
        jobpost_id: id,
        endpoint: '/api/jobpost/candidates',
        category: 'api'
    });
    
    try {
        if (!id) {
            const error = new Error('jobpost_id não fornecido para buscar candidaturas');
            logger.error('Erro ao buscar candidaturas - ID não fornecido', error, {
                jobpost_id: id,
                category: 'api'
            });
            throw error;
        }
        
        const url = `${environment.apiUrl}/api/jobpost/candidates?jobpost_id=${id}`;
        
        const response = await retryOnNetworkError(
            () => axios.get<any>(url, {
                headers: {
                    'Content-Type': 'application/json'
                },
                timeout: 15000
            }),
            1,
            {
                jobpost_id: id,
                endpoint: '/api/jobpost/candidates'
            }
        );

        if (response.status !== 200) {
            const error = new Error(`Resposta da API com status inesperado: ${response.status}`);
            logger.error('Erro ao buscar candidaturas - status inválido', error, {
                jobpost_id: id,
                status: response.status,
                statusText: response.statusText,
                category: 'api'
            });
            throw error;
        }

        if (!response.data) {
            const error = new Error('Resposta da API sem dados');
            logger.error('Erro ao buscar candidaturas - resposta vazia', error, {
                jobpost_id: id,
                category: 'api'
            });
            throw error;
        }

        if (!Array.isArray(response.data)) {
            const error = new Error('Resposta da API não é um array');
            logger.error('Erro ao buscar candidaturas - formato inválido', error, {
                jobpost_id: id,
                data_type: typeof response.data,
                category: 'api'
            });
            throw error;
        }
        
        logger.info('Candidaturas recuperadas com sucesso da API', {
            jobpost_id: id,
            count: response.data.length,
            category: 'api'
        });

        return response.data;
    } catch (error: any) {
        // Verificar se é erro de rede
        if (isNetworkError(error)) {
            logger.warn('Erro de rede ao buscar candidaturas - sem resposta do servidor', {
                jobpost_id: id,
                category: 'api',
                error_code: (error as any)?.code,
                error_message: (error as any)?.message,
                http_status: (error as any)?.response?.status
            });
            throw new NetworkError(
                'Erro de conexão ao buscar candidaturas. Verifique sua internet e tente novamente.',
                {
                    originalError: error,
                    httpStatus: (error as any)?.response?.status,
                    code: (error as any)?.code
                }
            );
        }

        if (error?.response) {
            logger.error('Erro HTTP ao buscar candidaturas', error, {
                jobpost_id: id,
                status: error.response.status,
                statusText: error.response.statusText,
                error_data: error.response.data,
                category: 'api'
            });
        } else if (error?.request) {
            logger.error('Erro de rede ao buscar candidaturas - sem resposta do servidor', error, {
                jobpost_id: id,
                category: 'api',
                error_code: (error as any)?.code,
                error_message: (error as any)?.message
            });
        } else {
            logger.error('Erro ao configurar requisição para buscar candidaturas', error, {
                jobpost_id: id,
                category: 'api'
            });
        }
        
        throw error;
    }
};